import type { GridColDef } from '@mui/x-data-grid';
import type { ProductsUsageReport } from '@vy/types';
import { isNotNullOrUndefined, vyDate, type ItemOf, type ValueOf } from '@vy/utils';
import { VyTableCell } from '../../../../../components/new-design/table/vy-table/VyTableCell';
import { getFormattedNumberWithCurrencySymbol } from '../../../../../services/currency-symbol-mapper';
import { formatDate } from '../../../../../utils/date';
import { formatNumberWithPrecision, formatPercentage } from '../../../../../utils/number';

export const PRODUCTS_USAGE_REPORT_KEYS = Object.freeze({
  CUSTOMER_NAME: 'customerName',
  ERP_ID: 'erpId',
  CONSUMED_UNITS: 'usageConsumptionUnitsAmount',
  PRODUCT_VARIANT_NAME: 'productVariantName',
  CONTRACT_START_DATE: 'contractStartDate',
  CONTRACT_END_DATE: 'contractEndDate',
  DAYS_TO_CONTRACT_END_DATE: 'daysToContractEnd',
  MAXIMUM_COMMITMENT: 'totalCommitmentCurrencyAmount',
  TOTAL_USAGE_CONSUMED: 'usageConsumptionCurrencyAmount',
  USAGE_PERCENTAGE: 'commitmentConsumptionPercentage',
  COMMERCIAL_TERMS_AMOUNT: 'commercialTermsAmount',
  TOTAL_COMMITMENT_UNITS: 'totalCommitmentUnitsAmount',
  CURRENCY: 'currency',
  CONTRACT_STATUS: 'contractStatus',
} satisfies Record<string, keyof ItemOf<ProductsUsageReport>>);

export type ProductsUsageReportColumnKeys = ValueOf<typeof PRODUCTS_USAGE_REPORT_KEYS>;

export const PRODUCTS_USAGE_REPORT_COLUMNS: GridColDef<ItemOf<ProductsUsageReport>>[] = [
  {
    field: PRODUCTS_USAGE_REPORT_KEYS.CUSTOMER_NAME,
    headerName: 'Customer',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    valueGetter: (_value, row) => row.customerName,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row: { customerName } }) => <VyTableCell value={customerName} />,
  },
  {
    field: PRODUCTS_USAGE_REPORT_KEYS.ERP_ID,
    headerName: 'Erp id',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 75,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row: { erpId } }) => <VyTableCell value={erpId ? erpId.slice(-5) : '--'} />,
  },
  {
    field: PRODUCTS_USAGE_REPORT_KEYS.PRODUCT_VARIANT_NAME,
    headerName: 'Product',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ value }) => <VyTableCell value={value} />,
  },
  {
    field: PRODUCTS_USAGE_REPORT_KEYS.CONTRACT_STATUS,
    headerName: 'Contract Status',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ value }) => <VyTableCell value={value} />,
  },
  {
    field: PRODUCTS_USAGE_REPORT_KEYS.CONTRACT_START_DATE,
    headerName: 'Contract start day',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'left',
    align: 'left',
    valueFormatter: value => vyDate(value).format('YYYY-MM-DD'),
    renderCell: ({ row: { contractStartDate } }) => <VyTableCell value={formatDate(contractStartDate)} />,
  },
  {
    field: PRODUCTS_USAGE_REPORT_KEYS.CONTRACT_END_DATE,
    headerName: 'Contract end date',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'left',
    align: 'left',
    valueFormatter: value => isNotNullOrUndefined(value) ? vyDate(value).format('YYYY-MM-DD') : 'Perpetual',
    renderCell: ({ row: { contractEndDate } }) => <VyTableCell value={isNotNullOrUndefined(contractEndDate) ? formatDate(contractEndDate) : '--'} />,
  },
  {
    field: PRODUCTS_USAGE_REPORT_KEYS.DAYS_TO_CONTRACT_END_DATE,
    headerName: 'Days to end',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 125,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row: { daysToContractEnd } }) => <VyTableCell value={daysToContractEnd ?? 'Unlimited'} />,
  },
  {
    field: PRODUCTS_USAGE_REPORT_KEYS.MAXIMUM_COMMITMENT,
    headerName: 'Minimum commitment',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'left',
    align: 'left',
    valueFormatter: value => formatNumberWithPrecision(value, 2),
    renderCell: ({ row: { currency, totalCommitmentCurrencyAmount } }) => (
      <VyTableCell
        value={
          isNotNullOrUndefined(totalCommitmentCurrencyAmount)
            ? getFormattedNumberWithCurrencySymbol(currency, totalCommitmentCurrencyAmount)
            : '--'
        }
      />
    ),
  },
  {
    field: PRODUCTS_USAGE_REPORT_KEYS.TOTAL_USAGE_CONSUMED,
    headerName: 'Total consumed',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'left',
    align: 'left',
    valueFormatter: value => formatNumberWithPrecision(value, 2),
    renderCell: ({ row: { currency, usageConsumptionCurrencyAmount } }) => (
      <VyTableCell
        value={
          isNotNullOrUndefined(usageConsumptionCurrencyAmount)
            ? getFormattedNumberWithCurrencySymbol(currency, usageConsumptionCurrencyAmount)
            : '--'
        }
      />
    ),
  },
  {
    field: PRODUCTS_USAGE_REPORT_KEYS.TOTAL_COMMITMENT_UNITS,
    headerName: 'Commitment units',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'left',
    align: 'left',
    valueFormatter: value => formatNumberWithPrecision(value, 2),
    renderCell: ({ row: { currency, totalCommitmentUnitsAmount } }) => (
      <VyTableCell
        value={
          isNotNullOrUndefined(totalCommitmentUnitsAmount)
            ? getFormattedNumberWithCurrencySymbol(currency, totalCommitmentUnitsAmount)
            : '--'
        }
      />
    ),
  },
  {
    field: PRODUCTS_USAGE_REPORT_KEYS.CONSUMED_UNITS,
    headerName: 'Consumed (Units)',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'left',
    align: 'left',
    valueFormatter: value => formatNumberWithPrecision(value, 2),
    renderCell: ({ row: { usageConsumptionUnitsAmount } }) => (
      <VyTableCell
        value={
          isNotNullOrUndefined(usageConsumptionUnitsAmount)
            ? formatNumberWithPrecision(usageConsumptionUnitsAmount, 2)
            : '--'
        }
      />
    ),
  },
  {
    field: PRODUCTS_USAGE_REPORT_KEYS.USAGE_PERCENTAGE,
    headerName: '% usage',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 100,
    headerAlign: 'left',
    align: 'left',
    valueFormatter: value => formatNumberWithPrecision(value, 2),
    renderCell: ({ row: { commitmentConsumptionPercentage } }) => (
      <VyTableCell
        value={
          isNotNullOrUndefined(commitmentConsumptionPercentage)
            ? formatPercentage(commitmentConsumptionPercentage)
            : '--'
        }
      />
    ),
  },
  {
    field: PRODUCTS_USAGE_REPORT_KEYS.COMMERCIAL_TERMS_AMOUNT,
    headerName: 'Commercials',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'left',
    align: 'left',
    valueFormatter: value => formatNumberWithPrecision(value, 2),
    renderCell: ({ row: { commercialTermsAmount, currency } }) => (
      <VyTableCell
        value={
          isNotNullOrUndefined(commercialTermsAmount)
            ? getFormattedNumberWithCurrencySymbol(currency, commercialTermsAmount)
            : '--'
        }
      />
    ),
  },
  {
    field: PRODUCTS_USAGE_REPORT_KEYS.CURRENCY,
    headerName: 'Currency',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 100,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row: { currency } }) => <VyTableCell value={currency} />,
  },
];
